// src/settings/Permission.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Alert,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Card,
    CardContent,
    Avatar,
    Stack,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Save,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    Lock as LockIcon,
    AdminPanelSettings as AdminIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Download as ExportIcon,
    Settings as SettingsIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIUserList,
    APIPermissionList,
    APIPermissionUpdate
} from '../../apiServices/APIs';
import { getUserDetails, recordsPerPage } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
// import { getUserRole } from '../../utils/Permission';

// Define available permissions
const AVAILABLE_PERMISSIONS = [
    { id: 'VIEW', label: 'View', icon: <ViewIcon />, description: 'Can view records', color: 'info' },
    { id: 'ADD', label: 'Add', icon: <AddIcon />, description: 'Can add new records', color: 'success' },
    { id: 'EDIT', label: 'Edit', icon: <EditIcon />, description: 'Can edit existing records', color: 'warning' },
    { id: 'DELETE', label: 'Delete', icon: <DeleteIcon />, description: 'Can delete records', color: 'error' },
    { id: 'EXPORT', label: 'Export', icon: <ExportIcon />, description: 'Can export data', color: 'secondary' },
    { id: 'PRINT', label: 'Print', icon: <PrintIcon />, description: 'Can print data', color: 'success' },
];

const Permission = () => {
    const theme = useTheme();
    const showToast = useCustomToast();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedUser, setSelectedUser] = useState(null);
    const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
    const [userPermissions, setUserPermissions] = useState({});
    const [tempPermissions, setTempPermissions] = useState([]);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);


    useEffect(() => {
        fetchUsers();
    }, []);

    // Fetch all users
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIUserList);
            
            if (result.success) {
                const usersList = Array.isArray(result.data) ? result.data : [];
                setUsers(usersList);
                setFilteredUsers(usersList);
                await fetchAllPermissions(usersList);
            } else {
                showToast("Error", result.message, "error");
            }
        } catch (error) {
            console.error("Fetch users error:", error);
            showToast("Error", "Failed to load users", "error");
        } finally {
            setLoading(false);
        }
    };

    // Fetch permissions for all users
    const fetchAllPermissions = async (usersList) => {
        try {
            const formDataToSend = new FormData();

            // Authenticated user performing the request
            formDataToSend.append('auth_user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));

            const result = await apiService.create(APIPermissionList, formDataToSend);

            if (result.success) {
                const permissionsMap = {};

                usersList.forEach(user => {
                    permissionsMap[user.id] = [];
                });

                result.data.forEach(permission => {
                    if (permissionsMap[permission.user_id] !== undefined) {
                        permissionsMap[permission.user_id] = permission.permissions || [];
                    }
                });

                setUserPermissions(permissionsMap);
            } else {
                const emptyPermissions = {};
                usersList.forEach(user => {
                    emptyPermissions[user.id] = [];
                });
                setUserPermissions(emptyPermissions);
            }

            setPermissionsLoaded(true);
        } catch (error) {
            console.error("Fetch permissions error:", error);

            const emptyPermissions = {};
            usersList.forEach(user => {
                emptyPermissions[user.id] = [];
            });

            setUserPermissions(emptyPermissions);
            setPermissionsLoaded(true);
        }
    };
    const fetchAllPermissions1 = async (usersList) => {
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            const result = await apiService.create(APIPermissionList,formDataToSend);
            // const result = await apiService.list(null,APIPermissionList);
            console.log("result"+JSON.stringify(result.data))
            if (result.success) {
                const permissionsMap = {};
                usersList.forEach(user => {
                    permissionsMap[user.id] = [];
                });
                
                result.data.forEach(permission => {
                    if (permissionsMap[permission.user_id] !== undefined) {
                        permissionsMap[permission.user_id] = permission.permissions || [];
                    }
                });
                
                setUserPermissions(permissionsMap);
            } else {
                const emptyPermissions = {};
                usersList.forEach(user => {
                    emptyPermissions[user.id] = [];
                });
                setUserPermissions(emptyPermissions);
            }
            setPermissionsLoaded(true);
        } catch (error) {
            console.error("Fetch permissions error:", error);
            const emptyPermissions = {};
            usersList.forEach(user => {
                emptyPermissions[user.id] = [];
            });
            setUserPermissions(emptyPermissions);
            setPermissionsLoaded(true);
        }
    };

    // Filter users based on search term
    useEffect(() => {
        if (searchTerm === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user =>
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.role?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
        setPage(0);
    }, [searchTerm, users]);

    // Handle open permission dialog
    const handleOpenPermissions = async (user) => {
        setSelectedUser(user);
        setTempPermissions(userPermissions[user.id] || []);
        setOpenPermissionDialog(true);
    };

    // Handle close permission dialog
    const handleClosePermissions = () => {
        setOpenPermissionDialog(false);
        setSelectedUser(null);
        setTempPermissions([]);
    };

    // Handle permission toggle
    const handlePermissionToggle = (permissionId) => {
        setTempPermissions(prev => {
            if (prev.includes(permissionId)) {
                return prev.filter(p => p !== permissionId);
            } else {
                return [...prev, permissionId];
            }
        });
    };

    // Handle select all permissions
    const handleSelectAll = () => {
        if (tempPermissions.length === AVAILABLE_PERMISSIONS.length) {
            setTempPermissions([]);
        } else {
            setTempPermissions(AVAILABLE_PERMISSIONS.map(p => p.id));
        }
    };

    // Handle save permissions
    const handleSavePermissions = async () => {
        if (!selectedUser) return;
        
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('user_id', selectedUser.id);
            // Send permissions as JSON string, backend will parse it
            formData.append('permissions', JSON.stringify(tempPermissions));
            formData.append('branch_id', getUserDetails("branchId"));
            formData.append('user_id_creator', getUserDetails("Id"));

            const result = await apiService.update(APIPermissionUpdate, formData);
            
            if (result.success) {
                setUserPermissions(prev => ({
                    ...prev,
                    [selectedUser.id]: tempPermissions
                }));
                showToast("Success", result.message, "success");
                handleClosePermissions();
                fetchUsers(); // Refresh the list
            } else {
                showToast("Errorss", result?.message || "Failed to save permissions", "error");
            }
        } catch (error) {
            console.error("Save permissions error:", error);
            showToast("Error", "Failed to save permissions", "error");
        } finally {
            setSaving(false);
        }
    };

    // Get permission chip color
    const getPermissionColor = (permissionId) => {
        const permission = AVAILABLE_PERMISSIONS.find(p => p.id === permissionId);
        return permission?.color || 'default';
    };

    // Get user role color
    const getRoleColor = (role) => {
        switch(role) {
            case 'AD': return 'error';
            case 'ST': return 'primary';
            default: return 'default';
        }
    };

    // Get role name
    const getRoleName = (roleId) => {
        const roles = {
            'AD': 'Admin',
            'ST': 'Staff ',
        };
        return roles[roleId] || roleId || 'Not Set';
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Clear search
    const clearSearch = () => {
        setSearchTerm('');
    };

    if (loading || !permissionsLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <SecurityIcon /> User Permissions Management ({users.length} Total)
                    </Typography>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                                endAdornment: searchTerm && (
                                    <IconButton size="small" onClick={clearSearch} sx={{ color: 'white' }}>
                                        <CloseIcon />
                                    </IconButton>
                                ),
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Assigned Permissions</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user, index) => {
                                    const permissions = userPermissions[user.id] || [];
                                    return (
                                        <TableRow
                                            key={user.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                                        {user.name?.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {user.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getRoleName(user.role)}
                                                    color={getRoleColor(user.role)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    {permissions.length > 0 ? (
                                                        permissions.map(perm => {
                                                            const permInfo = AVAILABLE_PERMISSIONS.find(p => p.id === perm);
                                                            return (
                                                                <Tooltip key={perm} title={permInfo?.description || perm}>
                                                                    <Chip
                                                                        icon={permInfo?.icon && React.cloneElement(permInfo.icon, { 
                                                                            sx: { color: 'inherit' }
                                                                        })}
                                                                        label={permInfo?.label || perm}
                                                                        color={getPermissionColor(perm)}
                                                                        size="small"
                                                                        sx={{ 
                                                                            m: 0.5,
                                                                            '& .MuiChip-icon': {
                                                                                color: 'inherit' // This makes the icon inherit the chip's color
                                                                            }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            );
                                                        })
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            No permissions assigned
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    onClick={() => handleOpenPermissions(user)}
                                                    color="primary"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.primary.light,
                                                            color: 'white'
                                                        }
                                                    }}
                                                >
                                                    <LockIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                    {filteredUsers.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <PersonIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No users found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Permission Assignment Dialog */}
            <Dialog
                open={openPermissionDialog}
                onClose={handleClosePermissions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">
                            <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Manage Permissions
                        </Typography>
                        <IconButton onClick={handleClosePermissions} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <Box sx={{ mt: 2 }}>
                            <Card sx={{ mb: 3, bgcolor: theme.palette.action.hover }}>
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                User Name
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {selectedUser.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Email
                                            </Typography>
                                            <Typography variant="body1">
                                                {selectedUser.email}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Role
                                            </Typography>
                                            <Chip
                                                label={getRoleName(selectedUser.role)}
                                                color={getRoleColor(selectedUser.role)}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Current Permissions
                                            </Typography>
                                            <Typography variant="body1">
                                                {tempPermissions.length} / {AVAILABLE_PERMISSIONS.length} assigned
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            <Divider sx={{ my: 2 }}>
                                <Chip label="Assign Permissions" icon={<LockIcon />} />
                            </Divider>

                            <FormGroup>
                                <Box sx={{ mb: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={tempPermissions.length === AVAILABLE_PERMISSIONS.length}
                                                indeterminate={tempPermissions.length > 0 && tempPermissions.length < AVAILABLE_PERMISSIONS.length}
                                                onChange={handleSelectAll}
                                            />
                                        }
                                        label="Select All Permissions"
                                    />
                                </Box>
                                <Grid container spacing={2}>
                                    {AVAILABLE_PERMISSIONS.map((permission) => (
                                        <Grid item xs={12} sm={6} md={4} key={permission.id}>
                                            <Card 
                                                variant="outlined" 
                                                sx={{ 
                                                    bgcolor: tempPermissions.includes(permission.id) ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                                                    }
                                                }}
                                                onClick={() => handlePermissionToggle(permission.id)}
                                            >
                                                <CardContent>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={tempPermissions.includes(permission.id)}
                                                                onChange={() => handlePermissionToggle(permission.id)}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                    {React.cloneElement(permission.icon, { 
                                                                        fontSize: 'small',
                                                                        sx: { color: `${permission.color}.main` }
                                                                    })}
                                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                                        {permission.label}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {permission.description}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </FormGroup>

                            <Alert severity="info" sx={{ mt: 3 }}>
                                <Typography variant="body2">
                                    <strong>Note:</strong> Permissions control what actions this user can perform in the system.
                                    {selectedUser.role === 'SA' && " Super Admin users automatically have all permissions."}
                                    {selectedUser.role === 'AG' && " Administrative General users have extensive permissions."}
                                    {(!selectedUser.role || selectedUser.role !== 'SA') && " You can assign specific permissions based on the user's role and responsibilities."}
                                </Typography>
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                        onClick={handleClosePermissions}
                        color="error"
                        variant="outlined"
                        startIcon={<CloseIcon />}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSavePermissions}
                        color="primary"
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Permissions'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Permission;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function for alpha color (add this at the top of your file or import)
const alpha = (color, opacity) => {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

